import { useMemo, useState } from "react";
import { siteSections, panelModes } from "@/mocks/panelContent";
import { FieldRow, LockedButton, PanelBadge, PanelCard, ReadOnlyNotice } from "../components/PanelUI";

export default function EditSiteSection() {
  const sections = useMemo(
    () => [...siteSections].sort((a, b) => a.position - b.position),
    [],
  );
  const [openId, setOpenId] = useState<string | null>(sections[0]?.id ?? null);

  return (
    <div className="flex flex-col gap-6">
      <ReadOnlyNotice text={panelModes.viewNotice} />

      <PanelCard
        title="Editar site"
        description="A ordem segue exatamente a ordem visual da página inicial, de cima para baixo. A estrutura, a grade e a arquitetura não podem ser alteradas."
      >
        <p className="mb-4 rounded-md border border-background-200 bg-background-100 px-3 py-2 text-xs leading-relaxed text-foreground-600">
          Podem ser alterados textos, imagens, cores configuráveis, links, títulos, subtítulos,
          valores, selos e chamadas, além da ordem dos produtos dentro das áreas existentes. Não é
          possível arrastar, remover ou criar seções.
        </p>

        <div className="flex flex-col gap-3">
          {sections.map((section) => {
            const open = openId === section.id;
            return (
              <div key={section.id} className="rounded-lg border border-background-200 bg-background-50">
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : section.id)}
                  aria-expanded={open}
                  className="flex w-full cursor-pointer items-center gap-3 px-4 py-3.5 text-left"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 font-heading text-sm text-primary-700">
                    {section.position}
                  </span>
                  <span className="flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-heading text-lg text-foreground-900">{section.name}</span>
                      <PanelBadge tone="neutral">Posição {section.position}</PanelBadge>
                    </span>
                    <span className="mt-0.5 block text-xs text-foreground-500">{section.description}</span>
                  </span>
                  <span className="flex h-6 w-6 items-center justify-center text-foreground-500">
                    <i className={open ? "ri-arrow-up-s-line text-xl" : "ri-arrow-down-s-line text-xl"} />
                  </span>
                </button>

                {open ? (
                  <div className="border-t border-background-200 px-4 py-4">
                    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                      <div>
                        <h4 className="mb-2 text-xs font-medium uppercase tracking-[0.14em] text-foreground-500">
                          Textos atuais
                        </h4>
                        <div className="rounded-md border border-background-200 bg-background-100 px-3 py-1">
                          {section.texts.length ? (
                            section.texts.map((text) => (
                              <FieldRow key={text.label} label={text.label} value={text.value} />
                            ))
                          ) : (
                            <p className="py-2 text-xs text-foreground-500">Sem textos editáveis nesta seção.</p>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col gap-4">
                        <div>
                          <h4 className="mb-2 text-xs font-medium uppercase tracking-[0.14em] text-foreground-500">
                            Cores utilizadas
                          </h4>
                          <div className="rounded-md border border-background-200 bg-background-100 px-3 py-1">
                            {section.colors.length ? (
                              section.colors.map((color) => (
                                <FieldRow key={color.label} label={color.label} value={color.value} />
                              ))
                            ) : (
                              <p className="py-2 text-xs text-foreground-500">—</p>
                            )}
                          </div>
                        </div>

                        <div>
                          <h4 className="mb-2 text-xs font-medium uppercase tracking-[0.14em] text-foreground-500">
                            Links e botões
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {section.links.length ? (
                              section.links.map((link) => (
                                <span
                                  key={link.label}
                                  className="rounded-md border border-background-200 bg-background-100 px-3 py-1.5 text-xs text-foreground-700"
                                >
                                  {link.label} → {link.href}
                                </span>
                              ))
                            ) : (
                              <p className="text-xs text-foreground-500">Sem links nesta seção.</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {section.images.length ? (
                      <div className="mt-5">
                        <h4 className="mb-2 text-xs font-medium uppercase tracking-[0.14em] text-foreground-500">
                          Imagens atuais
                        </h4>
                        <div className="flex flex-wrap gap-3">
                          {section.images.map((image) => (
                            <div key={image.label} className="w-[120px]">
                              <div className="h-[150px] w-full overflow-hidden rounded-md border border-background-200 bg-background-100">
                                <img
                                  src={image.url}
                                  alt={image.label}
                                  title={image.label}
                                  className="h-full w-full object-cover object-top"
                                />
                              </div>
                              <p className="mt-1 truncate text-[11px] text-foreground-500">{image.label}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="rounded-md border border-background-200 bg-background-100 p-3">
                        <p className="text-[11px] uppercase tracking-[0.14em] text-foreground-500">Versão desktop</p>
                        <p className="mt-1 text-xs text-foreground-700">{section.desktop}</p>
                      </div>
                      <div className="rounded-md border border-background-200 bg-background-100 p-3">
                        <p className="text-[11px] uppercase tracking-[0.14em] text-foreground-500">Versão mobile</p>
                        <p className="mt-1 text-xs text-foreground-700">{section.mobile}</p>
                      </div>
                    </div>

                    <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
                      <p className="text-[11px] text-foreground-500">
                        Última alteração: {section.lastChange} · Responsável: Vellora Studio
                      </p>
                      <div className="flex flex-wrap items-center gap-3">
                        <LockedButton icon="ri-edit-2-line">Editar esta seção</LockedButton>
                        <LockedButton icon="ri-history-line" hint="Restaurar versão anterior">
                          Restaurar
                        </LockedButton>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </PanelCard>
    </div>
  );
}